import { Poline, positionFunctions, Vector3 } from './poline';

export interface ColorSet {
  backgroundColor: string;
  textColor: string;
  iconColor: string;
}

/**
 * Generates a sophisticated color palette using Poline for smooth gradients
 * @param count Number of colors to generate
 */
export const generatePolinePalette = (count: number): string[] => {
  if (count === 1) {
    return ['hsl(240, 100%, 90%)'];
  }

  const anchorColors: Vector3[] = [
    [230, 0.58, 0.91],
    [40, 0.8, 0.97],
  ];

  const poline = new Poline({
    anchorColors,
    numPoints: count,
    positionFunctionX: positionFunctions.sinusoidalPosition,
    positionFunctionY: positionFunctions.quadraticPosition,
    positionFunctionZ: positionFunctions.smoothStepPosition,
  });

  return poline.colorsCSS;
};

/**
 * Shifts the lightness of an HSL color
 * @param hslColor HSL color string
 * @param lightnessShift Amount to shift lightness (positive = lighter, negative = darker)
 */
export const shiftLightness = (hslColor: string, lightnessShift: number): string => {
  // Try to match both formats: with and without spaces, and with decimal places
  const hslMatch = hslColor.match(/hsl\(([^,]+),\s*([^,]+)%?,\s*([^)]+)%?\)/);
  if (!hslMatch) {
    console.warn('Could not parse HSL color:', hslColor);
    return '#333333';
  }

  const [, hue, saturation, lightness] = hslMatch;
  const currentLightness = parseFloat(lightness.replace('%', ''));
  const newLightness = Math.max(5, Math.min(95, currentLightness + lightnessShift));

  return `hsl(${parseFloat(hue).toFixed(0)}, ${parseFloat(saturation.replace('%', '')).toFixed(
    0
  )}%, ${newLightness.toFixed(0)}%)`;
};

/**
 * Generates enhanced colors for choice options with better contrast
 * @param choices Array of choice options
 * @param baseColors Optional array of base colors to use
 */
export const generateColorPaletteSet = (count: number): ColorSet[] => {
  const backgroundPalette = generatePolinePalette(count);

  return backgroundPalette.map((color) => {
    const baseBgColor = color || 'hsl(240, 58%, 91%)';

    // Ensure we have a valid color, fallback to a safe light color
    let backgroundColor: string;
    let textColor: string;

    try {
      backgroundColor = shiftLightness(baseBgColor, 30);
      textColor = shiftLightness(baseBgColor, -40);
    } catch (error) {
      console.warn('Error processing color:', baseBgColor, error);
      backgroundColor = 'hsl(240, 30%, 85%)';
      textColor = 'hsl(240, 30%, 25%)';
    }

    const iconColor = textColor;

    return {
      backgroundColor,
      textColor,
      iconColor,
    };
  });
};
